import { useEffect, useState } from 'react';
import type { NotificationApiResponse } from '../../../../../../components/blackrock/advisor-center/_types/header.ts';
import orderNotificationsInTimeSpans from '../orderNotificationsInTimeSpans.ts';
import { LoadingState } from '../../../../../../components/blackrock/advisor-center/empty-state/EmptyStateProps.ts';
import useNotificationErrorToaster from './useNotificationErrorToaster.ts';

const fetchNotifications = async (
	apiUrl: string
): Promise<NotificationApiResponse | false> => {
	try {
		const response = await fetch(apiUrl);

		if (response?.status !== 200) {
			console.error(`Network response status: ${response?.status}`);
			return false;
		}

		return await response.json();
	} catch (error) {
		console.error('Request was failed:', error);
		return false;
	}
};

/**
 * This hook includes the business logic required for the notification feature of the header component.
 * It receives the api urls and some additionally needed data and returns useful callbacks and the state for the notifications.
 * @param notificationsUrl
 * @param markAllAsReadUrl
 * @param isLoggedIn
 * @param typeErrors
 */
export const useNotificationApi = (
	notificationsUrl: string | undefined,
	markAllAsReadUrl: string | undefined,
	isLoggedIn: boolean,
	typeErrors: Map<string, string> | undefined
) => {
	const [notificationsResponse, setNotificationsResponse] =
		useState<null | NotificationApiResponse>(null);

	const [loadNotificationsState, setLoadNotificationsState] =
		useState<LoadingState>(LoadingState.LOADING);

	/**
	 * This function fetches the notifications from the backend and sets the state accordingly.
	 * It only loads the items if they have not been loaded yet or the 'force' parameter is provided.
	 * @param force
	 */
	const loadNotifications = async (force = false) => {
		if (notificationsResponse && !force) return;

		if (!notificationsUrl || !isLoggedIn) return;

		const responseNotifications = await fetchNotifications(notificationsUrl);

		if (!responseNotifications) {
			setLoadNotificationsState(LoadingState.ERROR);
			return;
		}

		setNotificationsResponse(responseNotifications);

		setLoadNotificationsState(LoadingState.LOADED);
	};

	useEffect(() => {
		loadNotifications();
	}, [isLoggedIn]);

	useNotificationErrorToaster(notificationsResponse, typeErrors);

	/**
	 * This function retries to fetch the notifications.
	 */
	const retry = () => {
		setLoadNotificationsState(LoadingState.LOADING);
		setNotificationsResponse(null);
		loadNotifications(true);
	};

	const getLoggedInNewCountLabel = () =>
		!notificationsResponse?.unreadCount
			? ''
			: notificationsResponse.unreadCount < 100
				? String(notificationsResponse.unreadCount)
				: '99+';

	const newCountLabel = isLoggedIn ? getLoggedInNewCountLabel() : '1';

	/**
	 * This function calls backend api to mark all notifications as read and sets the React state beforehand.
	 * If the api call fails, it reverts the state to the previous value.
	 */
	const markAllAsRead =
		markAllAsReadUrl &&
		notificationsResponse &&
		notificationsResponse.unreadCount !== 0
			? async () => {
					setNotificationsResponse((prev) => {
						if (!prev) return null;

						return {
							unreadCount: 0,
							notifications: prev.notifications.map((elem) => ({
								...elem,
								state: 'READ',
							})),
						};
					});

					const revertState = () =>
						setNotificationsResponse(notificationsResponse);

					try {
						const response = await fetch(markAllAsReadUrl, {
							method: 'PATCH',
						});

						if (response?.status !== 200) {
							console.error(`Network response status: ${response?.status}`);

							revertState();
						}
					} catch (error) {
						console.error('Request was failed:', error);

						revertState();
					}
				}
			: null;

	const orderedNotifications = orderNotificationsInTimeSpans(
		notificationsResponse?.notifications ?? null
	);

	const isEmpty =
		!orderedNotifications ||
		[
			...orderedNotifications.lastMonth,
			...orderedNotifications.lastWeek,
			...orderedNotifications.latest,
		].length === 0;

	return {
		newCountLabel,
		notifications: orderedNotifications,
		isLoading: loadNotificationsState === LoadingState.LOADING,
		isError: loadNotificationsState === LoadingState.ERROR,
		isEmpty,
		retry,
		markAllAsRead,
	};
};

export default useNotificationApi;
